import util from '../util';
import Widget from './Widget';
import './scrollerWidget.scss';
import AVATAR_SVG from './avatar.svg';

export default class ScrollerWidget extends Widget {
	constructor(opts) {
		super(opts);
		this._items = [];
		this._offset = 0;
		this._setWidth = 0;
		this._animFrameId = null;
		this._lastTs = null;
		this._startAnimation();
	}

	_build() {
		super._build();
		this.container.classList.add('sc-widget');
		this._track = document.createElement('div');
		this._track.className = 'sc-track';
		this.container.appendChild(this._track);
	}

	destroy() {
		if(this._animFrameId !== null) {
			cancelAnimationFrame(this._animFrameId);
			this._animFrameId = null;
		}
		super.destroy();
	}

	// ── Settings ──────────────────────────────────────────────────────────────

	async init(initialData = {}) {
		await super.init(initialData);
		if(initialData.alerts) this.applyAlerts(initialData.alerts);
	}

	applySettings(settings) {
		super.applySettings(settings);
		this._rebuildTrack();
	}

	applyAlerts(alerts) {
		for(const alert of [...alerts].reverse()) {
			if(!alert.message) continue;
			this._pushAlert(alert.message);
		}
		this._rebuildTrack();
	}

	_generateCSS() {
		const id = this.container.id, s = this.settings;
		const textDecoration = [
			s.underlineText     ? 'underline'    : null,
			s.strikethroughText ? 'line-through' : null,
		].filter(Boolean).join(' ');

		return `
			.sc-widget#${id} {
				${s.fontFamily    ? `font-family: '${s.fontFamily}';`       : ''}
				${s.fontSize      ? `font-size: ${s.fontSize}px;`           : ''}
				${s.letterSpacing ? `letter-spacing: ${s.letterSpacing}px;` : ''}
				${s.boldText      ? 'font-weight: bold;'                    : ''}
				${s.italicText    ? 'font-style: italic;'                   : ''}
				${textDecoration  ? `text-decoration: ${textDecoration};`   : ''}
				${s.uppercaseText ? 'text-transform: uppercase;'            : ''}
				${s.textColor     ? `color: ${s.textColor};`                : ''}
			}
			.sc-widget#${id} .sc-item {
				${s.itemBg        ? `background: ${s.itemBg};`              : ''}
				${s.paddingTop    ? `padding-top: ${s.paddingTop}px;`       : ''}
				${s.paddingBottom ? `padding-bottom: ${s.paddingBottom}px;` : ''}
				${(s.blurRadius > 0 || s.spreadRadius > 0) ? `filter: ${util.generateSpreadShadowFilter(s.spreadRadius, s.blurRadius, s.shadowColor)};` : ''}
			}
			${s.extraCSS || ''}
		`;
	}

	_onAlert(data) {
		if(!data?.message) return;
		if(this.settings.donationsOnly && !(data.message.value > 0)) return;
		this._pushAlert(data.message);
		this._rebuildTrack();
	}

	_pushAlert(message) {
		const prepared = util.prepareMessage(message, {
			id: true,
			serviceId: true, serviceName: true, serviceIconUrl: true,
			avatarUrl: true, displayName: true,
			giftString: true, eventString: true,
		}, {
			giftTypes: this.giftTypes,
			eventTypes: this.eventTypes,
			serviceIcons: this.serviceIcons,
			user: this.userData,
		});
		const itemCount = this.settings.itemCount || 10;
		this._items.push(prepared);
		while(this._items.length > itemCount) this._items.shift();
	}

	_rebuildTrack() {
		this._track.innerHTML = '';
		if(!this._items.length) return;

		// Build the first two copies, then measure one set's width with float
		// precision to avoid integer-rounding drift accumulating into a visible jump.
		for(let copy = 0; copy < 2; copy++) {
			for(const item of this._items) {
				this._track.appendChild(this._buildItemElem(item));
			}
		}

		this._track.style.transform = 'none';
		const firstItem = this._track.children[0];
		const secondCopyFirst = this._track.children[this._items.length];
		this._setWidth = secondCopyFirst
			? secondCopyFirst.getBoundingClientRect().left - firstItem.getBoundingClientRect().left
			: this._track.scrollWidth / 2;

		// Add more copies until the track is wide enough to fill the container
		// with no gaps — needed when few items are shorter than the viewport.
		if(this._setWidth > 0) {
			const containerWidth = this.container.offsetWidth || 0;
			const needed = Math.ceil(containerWidth / this._setWidth) + 1;
			for(let copy = 2; copy < needed; copy++) {
				for(const item of this._items) {
					this._track.appendChild(this._buildItemElem(item));
				}
			}
		}

		while(this._offset < -this._setWidth) this._offset += this._setWidth;
		while(this._offset > 0) this._offset -= this._setWidth;

		this._track.style.transform = `translateX(${this._offset}px)`;
	}

	_buildItemElem(prepared) {
		const item = document.createElement('div');
		item.className = 'sc-item';

		const wrap = document.createElement('div');
		wrap.className = 'sc-avatar-wrap';
		const img = document.createElement('img');
		img.src = prepared.avatarUrl||AVATAR_SVG;
		img.alt = '';
		wrap.appendChild(img);
		if(prepared.serviceIconUrl) {
			const si = document.createElement('img');
			si.src = prepared.serviceIconUrl;
			si.alt = prepared.serviceName || '';
			si.className = 'sc-service-icon';
			wrap.appendChild(si);
		}
		item.appendChild(wrap);

		const nameSpan = document.createElement('span');
		nameSpan.className = 'sc-name';
		nameSpan.textContent = prepared.displayName || '';
		item.appendChild(nameSpan);

		const amountText = prepared.giftString || prepared.eventString;
		if(amountText) {
			const amountSpan = document.createElement('span');
			amountSpan.className = 'sc-amount';
			amountSpan.textContent = amountText;
			item.appendChild(amountSpan);
		}

		const sep = document.createElement('span');
		sep.className = 'sc-sep';
		sep.textContent = this.settings.separator !== undefined ? this.settings.separator : '\u00b7';
		item.appendChild(sep);

		return item;
	}

	_startAnimation() {
		const frame = ts => {
			this._animFrameId = requestAnimationFrame(frame);
			if(this._lastTs === null) {
				this._lastTs = ts;
				return;
			}
			const dt = Math.min((ts - this._lastTs) / 1000, 0.1);
			this._lastTs = ts;

			if(!this._items.length || this._setWidth === 0) return;

			const speed = typeof this.settings.speed === 'number' ? this.settings.speed : 60;
			if(speed === 0) return;

			this._offset -= speed * dt;

			while(this._offset < -this._setWidth) this._offset += this._setWidth;
			while(this._offset > 0) this._offset -= this._setWidth;

			this._track.style.transform = `translateX(${this._offset}px)`;
		};
		this._animFrameId = requestAnimationFrame(frame);
	}
}
